import React, { FC } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useSoapEditHistoryQuery } from '@/features/yakureki'
import { SoapEditHistoryItem } from '@/shared/apis/yakureki'

// セクション略称マッピング
const SECTION_LABELS: Record<string, string> = {
  subjectives: 'S（主観的情報）',
  objectives: 'O（客観的情報）',
  assessments: 'A（評価）',
  educational_plans: 'EP（教育計画）',
  care_plans: 'CP（ケア計画）',
  observational_plans: 'OP（観察計画）',
}

// edit_sourceに応じたChipの色
const EDIT_SOURCE_COLORS: Record<
  string,
  'primary' | 'secondary' | 'success' | 'info'
> = {
  manual_edit: 'primary',
  bulk_replace: 'secondary',
  ai_chat: 'info',
}

interface SoapEditHistoryDialogProps {
  open: boolean
  onClose: () => void
  yakurekiId?: string
}

/**
 * SOAP編集履歴ダイアログ
 *
 * 編集履歴一覧を表示し、各変更のdiffを色分けで表示する
 */
export const SoapEditHistoryDialog: FC<SoapEditHistoryDialogProps> = ({
  open,
  onClose,
  yakurekiId,
}) => {
  const {
    data: histories,
    isLoading,
    error,
  } = useSoapEditHistoryQuery(open ? yakurekiId : undefined)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh',
          maxHeight: 600,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
          backgroundColor: colorTokens.grey[100],
          borderBottom: `1px solid ${colorTokens.border.light}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon
            sx={{
              fontSize: 20,
              color: colorTokens.text.secondary,
            }}
          />
          <Typography>更新履歴</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          overflowY: 'auto',
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Box sx={{ p: 2 }}>
            <Typography
              sx={{
                color: 'error.main',
              }}
            >
              履歴の取得に失敗しました
            </Typography>
          </Box>
        )}
        {histories && histories.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Typography
              sx={{
                color: colorTokens.text.secondary,
              }}
            >
              更新履歴はありません
            </Typography>
          </Box>
        )}
        {histories &&
          histories.length > 0 &&
          histories.map((history) => (
            <HistoryEntry key={history.id} history={history} />
          ))}
      </DialogContent>
    </Dialog>
  )
}

/**
 * 履歴エントリコンポーネント
 */
const HistoryEntry: FC<{ history: SoapEditHistoryItem }> = ({ history }) => {
  const createdAt = new Date(history.created_at)
  const formattedDate = `${createdAt.getFullYear()}/${String(
    createdAt.getMonth() + 1
  ).padStart(2, '0')}/${String(createdAt.getDate()).padStart(2, '0')} ${String(
    createdAt.getHours()
  ).padStart(2, '0')}:${String(createdAt.getMinutes()).padStart(2, '0')}`

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${colorTokens.border.light}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* ヘッダー行 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={history.edit_source_label}
            size="small"
            color={EDIT_SOURCE_COLORS[history.edit_source] || 'default'}
            variant="outlined"
            sx={{ height: 24 }}
          />
          {history.summary && (
            <Typography
              sx={{
                color: colorTokens.text.secondary,
              }}
            >
              {history.summary}
            </Typography>
          )}
        </Box>
        <Typography
          sx={{
            color: colorTokens.text.secondary,
            whiteSpace: 'nowrap',
          }}
        >
          {formattedDate}
        </Typography>
      </Box>

      {/* Diff表示 */}
      {Object.entries(history.diff_json).map(([section, diff]) => (
        <SectionDiff key={section} section={section} diff={diff} />
      ))}
    </Box>
  )
}

/**
 * セクション別Diff表示コンポーネント
 */
const SectionDiff: FC<{
  section: string
  diff: {
    added?: string[]
    removed?: string[]
    edited?: { from: string; to: string }[]
    deselected?: string[]
    selected?: string[]
  }
}> = ({ section, diff }) => {
  const label = SECTION_LABELS[section] || section
  // diff_json は edit_source ごとに形状が異なり key が欠けうるため default [] で読む.
  const added = diff?.added ?? []
  const removed = diff?.removed ?? []
  const edited = diff?.edited ?? []
  const deselected = diff?.deselected ?? []
  const selected = diff?.selected ?? []

  return (
    <Box sx={{ ml: 1, mb: 0.5 }}>
      <Typography
        sx={{
          color: colorTokens.text.primary,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      {/* 追加 */}
      {added.map((text, i) => (
        <Box
          key={`added-${i}`}
          sx={{
            color: '#2e7d32',
            backgroundColor: 'rgba(46, 125, 50, 0.08)',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            mb: 0.25,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          + {text}
        </Box>
      ))}

      {/* 削除 */}
      {removed.map((text, i) => (
        <Box
          key={`removed-${i}`}
          sx={{
            color: '#c62828',
            backgroundColor: 'rgba(198, 40, 40, 0.08)',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            mb: 0.25,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          - {text}
        </Box>
      ))}

      {/* 編集 */}
      {edited.map((edit, i) => (
        <Box key={`edited-${i}`} sx={{ mb: 0.25 }}>
          <Box
            sx={{
              color: '#c62828',
              backgroundColor: 'rgba(198, 40, 40, 0.08)',
              borderRadius: '4px 4px 0 0',
              px: 1,
              py: 0.25,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            - {edit.from}
          </Box>
          <Box
            sx={{
              color: '#2e7d32',
              backgroundColor: 'rgba(46, 125, 50, 0.08)',
              borderRadius: '0 0 4px 4px',
              px: 1,
              py: 0.25,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            + {edit.to}
          </Box>
        </Box>
      ))}

      {/* 採否OFF = 行のチェックを外した (content 不変・コピー/転記から除外) */}
      {deselected.map((text, i) => (
        <Box
          key={`deselected-${i}`}
          sx={{
            color: colorTokens.text.secondary,
            backgroundColor: 'rgba(55, 53, 47, 0.06)',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            mb: 0.25,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          採否OFF {text}
        </Box>
      ))}

      {/* 採否ON = 一旦外した行を再びチェック (復帰) */}
      {selected.map((text, i) => (
        <Box
          key={`selected-${i}`}
          sx={{
            color: '#2e7d32',
            backgroundColor: 'rgba(46, 125, 50, 0.08)',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            mb: 0.25,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          採否ON {text}
        </Box>
      ))}
    </Box>
  )
}
